import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardCheck, FileText, Bot, LayoutTemplate, Store, CalendarCheck, ArrowRight, ShieldCheck, Landmark } from "lucide-react";

const CARDS = [
  { icon: ClipboardCheck, title: "Business Assessment", body: "Discover and validate your idea. A short guided assessment maps your goals, market and design preferences.", to: "/start", cta: "Start free assessment" },
  { icon: FileText, title: "Write your business plan", body: "A professional business plan in the format banks, the SBA and investors expect — with grant & funding help built in.", to: "/business-plan", cta: "Build my plan" },
  { icon: Landmark, title: "Check your funding readiness", body: "See honestly how prepared you appear for a lender conversation — SBA eligibility, DSCR and exactly what to fix first.", to: "/funding-readiness", cta: "Check my readiness" },
  { icon: Bot, title: "AI Business Strategist", body: "Get personalized guidance from Top Foster — recommendations, copy and strategy tailored to your business.", to: "/ai-concierge", cta: "Meet your strategist" },
  { icon: LayoutTemplate, title: "Build your website & brand", body: "Launch a professional site and brand identity ready to serve customers from day one.", to: "/services", cta: "See website packages" },
  { icon: Store, title: "See our work", body: "Explore real website designs we've built and pick one to tailor to your business.", to: "/marketplace", cta: "See our work" },
  { icon: CalendarCheck, title: "Talk to a strategist", body: "Prefer a human? Book a free consultation and we'll map your fastest path to launch.", to: "/book", cta: "Book a free consult" },
];

export default function PlanYourBusiness() {
  return (
    <div data-testid="plan-hub-page">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden grain">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 relative">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-5">
              <span className="h-px w-8 bg-gold" /> Plan · Build · Launch · Grow
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.05] max-w-3xl">
              Plan your business — <span className="text-gold">and actually build it.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-300 font-light mt-6 max-w-2xl leading-relaxed">
              Most guides just tell you what to do. ATF WebWorks does it with you — from validating your idea to a professional business plan, a live website and AI guidance every step of the way. Pick where you are and start.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hub cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}>
              <Link to={c.to} data-testid={`plan-card-${i}`}
                className="group flex flex-col h-full border border-white/10 bg-navy-800 p-7 transition-all hover:border-gold/50 hover:bg-navy-800/80 hover:-translate-y-1">
                <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold mb-5 transition-colors group-hover:bg-gold group-hover:text-navy"><c.icon size={22} /></div>
                <h3 className="font-heading text-2xl text-white mb-3 leading-tight">{c.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-1">{c.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-gold text-sm font-semibold">
                  {c.cta} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Assessment vs Business Plan — what's the difference */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-4" data-testid="plan-vs-strip">
        <div className="border border-white/10 bg-navy-800">
          <div className="px-6 md:px-8 py-5 border-b border-white/10">
            <h2 className="font-heading text-2xl text-white">Assessment or Business Plan — what's the difference?</h2>
            <p className="text-gray-400 text-sm mt-1">Two different steps. You only enter your details once — the plan is built from your assessment.</p>
          </div>
          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-3"><ClipboardCheck size={15} /> Business Assessment</div>
              <div className="text-white font-medium mb-2">"What should I build — and will it work?"</div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">Fast, free direction. We learn your business and point you the right way — nothing formal yet.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                {["Your Business Profile started", "A plain-English summary of your idea", "Recommended package & website designs", "A growth roadmap of what to do next"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="text-gold mt-0.5">·</span> {x}</li>
                ))}
              </ul>
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-3"><FileText size={15} /> Business Plan</div>
              <div className="text-white font-medium mb-2">"The official document that proves it — and helps you get funded."</div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">The formal, lender-ready deliverable built from your assessment.</p>
              <ul className="space-y-2 text-sm text-gray-300">
                {["A 9-section SBA / investor-ready plan", "3-year financial projections", "AI-matched grants & funding leads", "A polished PDF for banks, the SBA or investors"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="text-gold mt-0.5">·</span> {x}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="px-6 md:px-8 py-4 border-t border-white/10 text-sm text-gray-300">
            <span className="text-gold font-semibold">In short:</span> the Assessment tells you <span className="text-white">what</span> to build and <span className="text-white">why</span>. The Business Plan is the <span className="text-white">official document</span> that proves it.
          </div>
        </div>
      </section>

      {/* Veterans callout */}
      <section className="border-y border-white/10 bg-navy-800/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-3">
                <ShieldCheck size={15} /> Veterans Launch Initiative
              </div>
              <h2 className="font-heading text-3xl text-white mb-3">Transitioning out of the military?</h2>
              <p className="text-gray-400 max-w-2xl leading-relaxed">
                Before you take off the uniform, build your future. Our Veterans Launch Initiative helps service members leave active duty with a real, launched business — not just a DD-214.
              </p>
            </div>
            <Link to="/veterans" data-testid="plan-veterans-cta"
              className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-7 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors shrink-0">
              Explore the program <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
